import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import invariant from 'tiny-invariant'
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine'
import { draggable, dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter'
import { attachClosestEdge, extractClosestEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge'
import { useBoardsContext } from '../context/BoardsContext'
import useColumnCards from '../hooks/useColumnCards'
import ColumnHeader from './ColumnHeader'
import Card from './Card'
import AddCardModal from './AddCardModal'

function Column({ column }) {
  const columnRef = useRef(null)
  const headerRef = useRef(null)
  const sentinelRef = useRef(null)
  const cardsContainerRef = useRef(null)
  const { canEdit, canManage, boardSearch } = useBoardsContext()
  const {
    cards, totalCount, unseenCount,
    isSearchMode, hasNextPage, isFetchingNextPage, fetchNextPage
  } = useColumnCards(column, boardSearch.debouncedQuery)

  const [isDraggedOver, setIsDraggedOver] = useState(false)
  const [closestEdge, setClosestEdge] = useState(null)
  const [showAddCard, setShowAddCard] = useState(false)

  useEffect(() => {
    if (!hasNextPage) return
    if (!sentinelRef.current) return

    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage()
      }
    }, {
      root: cardsContainerRef.current || null,
      rootMargin: '200px'
    })
    observer.observe(sentinelRef.current)
    return () => observer.disconnect()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  useEffect(() => {
    const columnElm = columnRef.current
    invariant(columnElm)

    const cleanups = [
      dropTargetForElements({
        element: columnElm,
        getData: ({ input, element }) => {
          const data = { type: 'column', columnId: column.id }
          return attachClosestEdge(data, { input, element, allowedEdges: ['left', 'right'] })
        },
        getIsSticky: () => true,
        onDragEnter: (args) => {
          setIsDraggedOver(true)
          if (args.source.data.type === 'column') {
            setClosestEdge(extractClosestEdge(args.self.data))
          }
        },
        onDrag: (args) => {
          if (args.source.data.type === 'column') {
            setClosestEdge(extractClosestEdge(args.self.data))
          }
        },
        onDragLeave: () => { setIsDraggedOver(false); setClosestEdge(null) },
        onDrop: () => { setIsDraggedOver(false); setClosestEdge(null) }
      })
    ]

    if (canManage && headerRef.current) {
      cleanups.push(
        draggable({
          element: headerRef.current,
          getInitialData: () => ({ type: 'column', columnId: column.id }),
        })
      )
    }

    const cleanup = combine(...cleanups)
    return () => { if (typeof cleanup === 'function') cleanup() }
  }, [column.id, canManage])

  return (
    <div
      ref={columnRef}
      className={classNames('kb-column', { 'is-dragged-over': isDraggedOver })}
      style={{ position: 'relative', maxHeight: '100%' }}
    >
      <ColumnHeader
        column={column}
        headerRef={headerRef}
        totalCount={totalCount}
        unseenCount={unseenCount}
        searchActive={isSearchMode}
      />
      <div ref={cardsContainerRef} className="kb-column-cards">
        {cards.map((card, index) => (
          <Card key={card.id} card={card} columnId={column.id} index={index} />
        ))}
        {isSearchMode && cards.length === 0 && !isFetchingNextPage && (
          <div className="text-xs text-gray-400 italic" style={{ padding: '6px 8px' }}>
            No matches in this column
          </div>
        )}
        {hasNextPage && (
          <div ref={sentinelRef} className="text-xs text-gray-400 italic" style={{ padding: '6px 8px' }}>
            {isFetchingNextPage ? 'Loading more…' : ''}
          </div>
        )}
      </div>
      {canEdit && (
        <div className="kb-column-footer">
          <button onClick={() => setShowAddCard(true)} data-tour="add-card">
            +Add a card
          </button>
        </div>
      )}
      {showAddCard && (
        <AddCardModal columnId={column.id} onClose={() => setShowAddCard(false)} />
      )}
      {closestEdge && (
        <div className={`kb-col-edge kb-col-edge--${closestEdge}`} />
      )}
    </div>
  )
}

Column.propTypes = {
  column: PropTypes.object.isRequired
}

export default Column
